import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Plus, Search, Filter, Edit, Trash2, Trophy, Percent, Target } from 'lucide-react';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import { PERSIAN_TEXTS } from '../../constants/persianTexts';
import prizeStructureService from '../../services/prizeStructureService';
import PrizeStructureModal from '../../components/Modals/PrizeStructureModal';
import toast from 'react-hot-toast';

const PrizeStructuresContainer = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 20px;
`;

const Title = styled.h1`
  color: #ffffff;
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  background: linear-gradient(135deg, #00d4ff 0%, #4ecdc4 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
  flex-wrap: wrap;
`;

const PrizeStructuresList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 30px;
`;

const PrizeStructureCard = styled(Card)`
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  border: 1px solid #2d2d44;
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 212, 255, 0.15);
    border-color: #00d4ff;
  }
`;

const PrizeStructureContent = styled.div`
  padding: 25px;
`;

const PrizeStructureHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
`;

const PrizeStructureInfo = styled.div`
  flex: 1;
`;

const PrizeStructureName = styled.h3`
  color: #ffffff;
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 8px 0;
`;

const OrganizerCut = styled.div`
  color: #4ecdc4;
  font-size: 16px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PrizeStructureActions = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const ActionButton = styled.button`
  background: ${props => props.variant === 'edit' ? 'linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%)' : 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)'};
  border: none;
  border-radius: 8px;
  padding: 8px 12px;
  color: white;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }
`;

const PlacementsSection = styled.div`
  margin-top: 20px;
`;

const PlacementsTitle = styled.h4`
  color: #b8b8d1;
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 15px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PlacementsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
`;

const PlacementItem = styled.div`
  background: linear-gradient(135deg, #2d2d44 0%, #1a1a2e 100%);
  border: 1px solid #3d3d5a;
  border-radius: 12px;
  padding: 15px;
  text-align: center;
`;

const PlacementRank = styled.div`
  color: #00d4ff;
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 8px;
`;

const PlacementPercent = styled.div`
  color: #4ecdc4;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 4px;
`;

const PlacementScore = styled.div`
  color: #b8b8d1;
  font-size: 14px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6c6c8a;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 20px;
`;

const EmptyTitle = styled.h3`
  color: #b8b8d1;
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 10px 0;
`;

const EmptyDescription = styled.p`
  color: #6c6c8a;
  font-size: 16px;
  margin: 0;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #b8b8d1;
`;

// Helper function for placement suffix
const getPlacementSuffix = (row) => {
  if (row === 1) return 'ام';
  if (row === 2) return 'ام';
  if (row === 3) return 'ام';
  return 'ام';
};

const PrizeStructures = () => {
  const [prizeStructures, setPrizeStructures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPrizeStructureModal, setShowPrizeStructureModal] = useState(false);
  const [selectedPrizeStructure, setSelectedPrizeStructure] = useState(null);

  useEffect(() => {
    fetchPrizeStructures();
  }, []);

  const fetchPrizeStructures = async () => {
    try {
      setLoading(true);
      const data = await prizeStructureService.getPrizeStructures();
      setPrizeStructures(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching prize structures:', error);
      toast.error('خطا در بارگذاری ساختارهای جایزه');
    } finally {
      setLoading(false);
    }
  };

  const filteredPrizeStructures = prizeStructures.filter(structure =>
    structure.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddPrizeStructure = () => {
    setSelectedPrizeStructure(null);
    setShowPrizeStructureModal(true);
  };

  const handleEditPrizeStructure = (structure) => {
    setSelectedPrizeStructure(structure);
    setShowPrizeStructureModal(true);
  };

  const handleDeletePrizeStructure = async (structure) => {
    if (window.confirm('آیا از حذف این ساختار جایزه اطمینان دارید؟')) {
      try {
        await prizeStructureService.deletePrizeStructure(structure.id);
        toast.success('ساختار جایزه با موفقیت حذف شد');
        fetchPrizeStructures();
      } catch (error) {
        toast.error('خطا در حذف ساختار جایزه');
        console.error('Error deleting prize structure:', error);
      }
    }
  };

  const handlePrizeStructureModalClose = () => {
    setShowPrizeStructureModal(false);
    setSelectedPrizeStructure(null);
  };

  const handlePrizeStructureModalSuccess = () => {
    fetchPrizeStructures();
  };

  if (loading) {
    return (
      <PrizeStructuresContainer>
        <LoadingSpinner>
          <div>در حال بارگذاری...</div>
        </LoadingSpinner>
      </PrizeStructuresContainer>
    );
  }

  return (
    <PrizeStructuresContainer>
      <Header>
        <Title>مدیریت ساختارهای جایزه</Title>
        <SearchContainer>
          <Input
            type="text"
            placeholder="جستجو در ساختارهای جایزه..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={<Search size={16} />}
          />
          <Button variant="outline" icon={<Filter size={16} />}>
            فیلتر
          </Button>
          <Button variant="primary" onClick={handleAddPrizeStructure} icon={<Plus size={16} />}>
            افزودن ساختار جایزه
          </Button>
        </SearchContainer>
      </Header>

      <PrizeStructureModal
        show={showPrizeStructureModal}
        prizeStructure={selectedPrizeStructure}
        onClose={handlePrizeStructureModalClose}
        onSuccess={handlePrizeStructureModalSuccess}
      />

      {filteredPrizeStructures.length === 0 ? (
        <EmptyState>
          <EmptyIcon>🏆</EmptyIcon>
          <EmptyTitle>هیچ ساختار جایزه‌ای یافت نشد</EmptyTitle>
          <EmptyDescription>
            {searchTerm ? 'هیچ ساختار جایزه‌ای با این جستجو یافت نشد' : 'هنوز هیچ ساختار جایزه‌ای ایجاد نشده است'}
          </EmptyDescription>
        </EmptyState>
      ) : (
        <PrizeStructuresList>
          {filteredPrizeStructures.map((structure) => (
            <motion.div
              key={structure.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <PrizeStructureCard>
                <PrizeStructureContent>
                  <PrizeStructureHeader>
                    <PrizeStructureInfo>
                      <PrizeStructureName>{structure.name}</PrizeStructureName>
                      <OrganizerCut>
                        <Percent size={16} />
                        سهم برگزارکننده: {structure.organizerCutPercent}%
                      </OrganizerCut>
                    </PrizeStructureInfo>
                    
                    <PrizeStructureActions>
                      <ActionButton variant="edit" onClick={() => handleEditPrizeStructure(structure)}>
                        <Edit size={14} />
                        ویرایش
                      </ActionButton>
                      <ActionButton variant="delete" onClick={() => handleDeletePrizeStructure(structure)}>
                        <Trash2 size={14} />
                        حذف
                      </ActionButton>
                    </PrizeStructureActions>
                  </PrizeStructureHeader>
                  
                  {structure.placements && structure.placements.length > 0 && (
                    <PlacementsSection>
                      <PlacementsTitle>
                        <Target size={16} />
                        رتبه‌بندی جوایز
                      </PlacementsTitle>
                      <PlacementsList>
                        {structure.placements
                          .sort((a, b) => a.row - b.row)
                          .map((placement, index) => (
                            <PlacementItem key={index}>
                              <PlacementRank>
                                {placement.row}{getPlacementSuffix(placement.row)}
                              </PlacementRank>
                              <PlacementPercent>
                                {placement.percent}%
                              </PlacementPercent>
                              <PlacementScore>
                                امتیاز: {placement.score}
                              </PlacementScore>
                            </PlacementItem>
                          ))}
                      </PlacementsList>
                    </PlacementsSection>
                  )}
                </PrizeStructureContent>
              </PrizeStructureCard>
            </motion.div>
          ))}
        </PrizeStructuresList>
      )}
    </PrizeStructuresContainer>
  );
};

export default PrizeStructures;
